import { Suspense, lazy, type ReactNode } from 'react';

import { pathnameOf, useLocale, useRememberLocale } from '../shared/i18n';
import { PublicLayout } from '../shared/layout';
import { useCurrentUrl } from '../shared/lib';

/**
 * The admin shell, behind a lazy boundary.
 *
 * `RootLayout` is shared by every route, so anything imported here statically
 * ends up in the chunk graph that public pages reach. `React.lazy` turns that
 * into a dynamic import: the browser fetches the admin chunk when — and only
 * when — the branch below actually renders it. That is what HSQ-044's
 * "admin chunks absent from public bundles" acceptance criterion asks for, and
 * it is verified by bundle analysis rather than by inspection.
 */
const AdminShell = lazy(() => import('./AdminShell'));

/**
 * Chooses the shell for the route being rendered.
 *
 * Vike core has no nested-layout config — that is a `vike-react` feature, and
 * this project uses Vike directly so the SSR tier keeps control of the document
 * shell (`lang`, `dir`, the pre-paint theme script and its CSP nonce). One
 * branch on the path is a smaller price than a second rendering abstraction.
 *
 * The split is the same one that governs the whole application: `/admin` is a
 * client-only SPA, everything else is server-rendered public content.
 */
export function RootLayout({ children }: { children: ReactNode }) {
  const currentUrl = useCurrentUrl();
  const urlPathname = pathnameOf(currentUrl);

  // Mounted above the route split so the preference is recorded on every page,
  // including the one a language switch lands on. The switcher itself is a
  // plain link and has nowhere to put this.
  useRememberLocale(useLocale());

  if (urlPathname.startsWith('/admin')) {
    return (
      // No fallback UI. The admin group is `ssr: false`, so the browser is
      // already showing an empty shell at this point; a spinner here would
      // replace nothing with something and then flash away.
      <Suspense fallback={null}>
        <AdminShell pathname={urlPathname} currentUrl={currentUrl}>
          {children}
        </AdminShell>
      </Suspense>
    );
  }

  return <PublicLayout currentUrl={currentUrl}>{children}</PublicLayout>;
}
